import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  // ARCH-025: 15s timeout prevents the UI from blocking indefinitely if the backend hangs.
  timeout: 15000,
});

export function unwrapResponseData(body) {
  if (
    body &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    Object.prototype.hasOwnProperty.call(body, 'data')
  ) {
    return body.data;
  }

  return body;
}

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth-401 handling: typed allowlist of public-prefix paths that should
// never trigger a refresh+retry or a logout. Route renames touching this
// list need a deliberate edit instead of a fragile substring search.
const PUBLIC_AUTH_EXEMPT_PREFIXES = ['/auth/', '/profile/public/', '/profile/full/'];

function isExemptUrl(url) {
  if (!url) return false;

  let pathname;

  try {
    const baseUrl = getApiBaseUrl();
    const parsedUrl = new URL(url, baseUrl);
    pathname = parsedUrl.pathname;

    const apiBasePath = baseUrl.pathname.replace(/\/$/, '');
    if (apiBasePath && apiBasePath !== '/' && pathname.startsWith(`${apiBasePath}/`)) {
      pathname = pathname.slice(apiBasePath.length);
    }
  } catch {
    pathname = String(url).split(/[?#]/)[0] || '';
  }

  if (!pathname.startsWith('/')) pathname = `/${pathname}`;
  return PUBLIC_AUTH_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function getApiBaseUrl() {
  const withTrailingSlash = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;

  try {
    return new URL(withTrailingSlash);
  } catch {
    const path = withTrailingSlash.startsWith('/') ? withTrailingSlash : `/${withTrailingSlash}`;
    return new URL(path, 'http://localhost');
  }
}

// SHO-38: redirect with `?reason=session-expired` so the Login page can
// surface a friendly explanation instead of a generic toast. All paths
// into this function come from the response interceptor, which only fires
// after a 401 — meaning the user did have an active session.
function clearAuthAndRedirect() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  window.location.href = '/login?reason=session-expired';
}

let refreshPromise = null;

function refreshAuthToken(refreshToken) {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh`, { refreshToken }, { timeout: 15000 })
      .then((refreshRes) => {
        const data = unwrapResponseData(refreshRes.data);
        const newToken = data?.token;
        const newRefresh = data?.refreshToken;

        if (!newToken) throw new Error('Refresh response missing access token');

        localStorage.setItem('token', newToken);
        if (newRefresh) localStorage.setItem('refresh_token', newRefresh);

        return { token: newToken, refreshToken: newRefresh };
      })
      .catch((refreshErr) => {
        clearAuthAndRedirect();
        throw refreshErr;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// 401 handling: try `/auth/refresh` once with the stored refresh token
// before falling back to logout. The retry uses a bare axios call to
// avoid recursing back into this interceptor; the original request is
// then replayed via `api(...)` so the request interceptor picks up the
// freshly stored access token.
//
// Concurrency note: all simultaneous 401s share one in-flight refresh.
// This prevents a stale failed refresh from clearing tokens written by a
// newer successful refresh while still keeping each original request's
// one-retry guard local to its config.
api.interceptors.response.use(
  (response) => ({
    ...response,
    data: unwrapResponseData(response.data),
  }),
  async (error) => {
    const status = error.response?.status;
    const config = error.config || {};
    const url = config.url || '';

    if (status !== 401 || isExemptUrl(url)) {
      return Promise.reject(error);
    }

    // Already retried this exact request — fall through to logout.
    if (config._retried) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    config._retried = true;

    try {
      await refreshAuthToken(refreshToken);

      // Retry the original request — the request interceptor reads the
      // new token from localStorage on each invocation.
      return api(config);
    } catch {
      return Promise.reject(error);
    }
  }
);

// Auth
export const register = (userData) => api.post('/auth/register', userData);
export const login = (credentials) => api.post('/auth/login', credentials);

// Users
export const getCurrentUser = () => api.get('/users/me');
export const completeOnboarding = () => api.put('/users/me/onboarding');
export const getUser = (userId) => api.get(`/users/${userId}`);
export const updateProfile = (userId, profileData) => api.put(`/users/${userId}`, profileData);
// ARCH-024: use axios `params` so the query value is properly encoded — `&`, `#`, `?`
// in user input previously bypassed encoding and produced malformed URLs.
export const searchUsers = (query) => api.get('/users', { params: { search: query } });

// Posts
export const createPost = (postData) => api.post('/posts', postData);
export const getFeed = (limit = 20, offset = 0) =>
  api.get(`/posts/feed?limit=${limit}&offset=${offset}`);
export const getUserPosts = (userId) => api.get(`/posts/user/${userId}`);
export const updatePost = (postId, postData) => api.put(`/posts/${postId}`, postData);
export const deletePost = (postId) => api.delete(`/posts/${postId}`);
export const repostPost = (postId, comment) => api.post(`/posts/${postId}/repost`, { comment });
export const unrepostPost = (postId) => api.delete(`/posts/${postId}/repost`);
export const bookmarkPost = (postId) => api.post(`/posts/${postId}/bookmark`);
export const unbookmarkPost = (postId) => api.delete(`/posts/${postId}/bookmark`);
export const getBookmarkedPosts = (limit, offset) =>
  api.get(`/posts/bookmarks?limit=${limit || 20}&offset=${offset || 0}`);
export const likePost = (postId) => api.post(`/posts/${postId}/like`);
export const unlikePost = (postId) => api.delete(`/posts/${postId}/like`);
export const getComments = (postId) => api.get(`/posts/${postId}/comments`);
export const addComment = (postId, content, parentId) =>
  api.post(`/posts/${postId}/comments`, { content, parent_id: parentId });
export const deleteComment = (commentId) => api.delete(`/posts/comments/${commentId}`);

// Follows
export const followUser = (userId, message) =>
  api.post('/follows', { following_id: userId, ...(message && { message }) });
export const unfollowUser = (userId) => api.delete(`/follows/${userId}`);
export const checkFollowing = (userId) => api.get(`/follows/check/${userId}`);
export const getFollowers = (userId) => api.get(`/follows/followers/${userId}`);
export const getFollowing = (userId) => api.get(`/follows/following/${userId}`);

// Messages
export const sendMessage = (messageData) => api.post('/messages', messageData);
// messageData can include: { receiver_id, content, media_url, media_type }
export const getConversation = (userId) => api.get(`/messages/conversation/${userId}`);
export const getConversations = () => api.get('/messages/conversations');
export const markMessagesRead = (userId) => api.put(`/messages/read/${userId}`);
export const getUnreadCount = () => api.get('/messages/unread-count');

// Jobs
export const getJobs = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.type) params.append('type', filters.type);
  if (filters.location) params.append('location', filters.location);
  if (filters.payRange) params.append('payRange', filters.payRange);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);

  return api.get(`/jobs?${params.toString()}`);
};
export const getJob = (jobId) => api.get(`/jobs/${jobId}`);
export const createJob = (jobData) => api.post('/jobs', jobData);
export const updateJob = (jobId, jobData) => api.put(`/jobs/${jobId}`, jobData);
export const deleteJob = (jobId) => api.delete(`/jobs/${jobId}`);
export const seedJobs = () => api.post('/jobs/seed');

// Resolve media URLs — converts relative /uploads/... paths to absolute backend URLs
const BACKEND_BASE = API_BASE_URL.replace(/\/api$/, '');
export const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND_BASE}${url}`;
};

// Upload
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData);
};
export const uploadMultipleFiles = (files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return api.post('/upload/multiple', formData);
};

// Resume Scanner
export const scanResume = (file, resumeType = 'general') => {
  const formData = new FormData();
  formData.append('resume', file);
  formData.append('resume_type', resumeType);
  return api.post('/resume/scan', formData);
};

// Network
export const getNetworkSuggestions = (limit = 20, offset = 0) =>
  api.get(`/network/suggestions?limit=${limit}&offset=${offset}`);
export const getPendingConnections = () => api.get('/network/pending');
export const denyPendingConnection = (userId) => api.delete(`/network/pending/${userId}`);
export const getMutualConnections = (userId) => api.get(`/network/mutual/${userId}`);
export const browseNetwork = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.profession) params.append('profession', filters.profession);
  if (filters.location) params.append('location', filters.location);
  if (filters.q) params.append('q', filters.q);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  return api.get(`/network/browse?${params.toString()}`);
};
export const getProductionPage = (name, company) => {
  const params = new URLSearchParams({ name });
  if (company) params.append('company', company);
  return api.get(`/network/production?${params.toString()}`);
};
export const browseProductions = (q, limit) => {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  if (limit) params.append('limit', limit);
  return api.get(`/network/productions?${params.toString()}`);
};
export const getNetworkFollowers = () => api.get('/network/followers');
export const getNetworkFollowing = () => api.get('/network/following');
export const addCredit = (creditData) => api.post('/network/credits', creditData);
export const getUserCredits = (userId) => api.get(`/network/credits/${userId}`);

// Profile - Settings (Visual Customization)
export const getProfileSettings = (userId) => api.get(`/profile/settings/${userId}`);
export const updateProfileSettings = (settings) => api.put('/profile/settings', settings);

// Profile - Performer Info
export const getPerformerProfile = (userId) => api.get(`/profile/performer/${userId}`);
export const updatePerformerProfile = (profile) => api.put('/profile/performer', profile);

// Profile - Credits (Work History)
export const getCredits = (userId) => api.get(`/profile/credits/${userId}`);
export const getCreditsGrouped = (userId) => api.get(`/profile/credits/${userId}/grouped`);
export const addProfileCredit = (credit) => api.post('/profile/credits', credit);
export const updateProfileCredit = (id, credit) => api.put(`/profile/credits/${id}`, credit);
export const deleteProfileCredit = (id) => api.delete(`/profile/credits/${id}`);
export const toggleFeaturedCredit = (id) => api.put(`/profile/credits/${id}/featured`);
export const getFeaturedCredits = (userId) => api.get(`/profile/credits/${userId}/featured`);

// Profile - Skills
export const getSkills = (userId) => api.get(`/profile/skills/${userId}`);
export const getSkillsGrouped = (userId) => api.get(`/profile/skills/${userId}/grouped`);
export const addSkill = (skill) => api.post('/profile/skills', skill);
export const deleteSkill = (id) => api.delete(`/profile/skills/${id}`);
export const searchBySkill = (skill, category) => {
  const params = new URLSearchParams();
  if (skill) params.append('skill', skill);
  if (category) params.append('category', category);
  return api.get(`/profile/search/skills?${params.toString()}`);
};

// Profile - Media
export const getMedia = (userId) => api.get(`/profile/media/${userId}`);
export const addMedia = (media) => api.post('/profile/media', media);
export const updateMedia = (id, media) => api.put(`/profile/media/${id}`, media);
export const deleteMedia = (id) => api.delete(`/profile/media/${id}`);

// Profile - Contact Links
export const getContactLinks = (userId) => api.get(`/profile/links/${userId}`);
export const addContactLink = (link) => api.post('/profile/links', link);
export const updateContactLink = (id, link) => api.put(`/profile/links/${id}`, link);
export const deleteContactLink = (id) => api.delete(`/profile/links/${id}`);

// Profile - Professions (multiple per user)
export const getProfessions = (userId) => api.get(`/profile/professions/${userId}`);
export const addProfession = (profession) => api.post('/profile/professions', profession);
export const deleteProfession = (id) => api.delete(`/profile/professions/${id}`);
export const setPrimaryProfession = (id) => api.put(`/profile/professions/${id}/primary`);

// Profile - Location & Industry
export const getUserLocation = (userId) => api.get(`/profile/location/${userId}`);
export const updateUserLocation = (location) => api.put('/profile/location', { location });
export const updateUserIndustry = (industry) => api.put('/profile/industry', { industry });

// Profile - Full Profile (all data combined)
export const getFullProfile = (userId) => api.get(`/profile/full/${userId}`);

// Profile - Public (by slug, no auth required)
export const getPublicProfile = (slug) => api.get(`/profile/public/${slug}`);

// Profile - Slug Management
export const getSlug = () => api.get('/profile/slug');
export const updateSlug = (slug) => api.put('/profile/slug', { slug });
export const checkSlugAvailable = (slug) => api.get(`/profile/slug/check/${slug}`);

// Availability
export const getMyAvailability = () => api.get('/availability/me');
export const getUserAvailability = (userId) => api.get(`/availability/user/${userId}`);
export const updateAvailabilityStatus = (status, details, locationPref) =>
  api.put('/availability/status', { status, details, location_pref: locationPref });
export const clearAvailabilityStatus = () => api.delete('/availability/status');
export const addAvailabilityBlock = (block) => api.post('/availability/blocks', block);
export const updateAvailabilityBlock = (id, block) => api.put(`/availability/blocks/${id}`, block);
export const deleteAvailabilityBlock = (id) => api.delete(`/availability/blocks/${id}`);
export const searchByAvailability = (from, to, status) => {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  if (status) params.append('status', status);
  return api.get(`/availability/search?${params.toString()}`);
};

export default api;
